import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    Paper,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    Divider,
} from '@mui/material';

export interface Step3CmdrsData {
    existingCouncils: string[];
    otherExistingCouncil: string;
    seatedCouncils: string[];
    otherSeatedCouncil: string;
    isConsortiumMember: boolean | null;
    participatesActivities: boolean | null;
    activitiesDescription: string;
    hasSusaf: boolean | null;
}

interface Step3Props {
    hasCmdrs: boolean | null;
    disabled?: boolean;
    onNext: () => void;
    onBack: () => void;
    onChange: (data: Step3CmdrsData) => void;
    data: Step3CmdrsData;
}

const councilOptions = [
    { label: 'Conselho Municipal de Segurança Alimentar e Nutricional', value: 'food_security' },
    { label: 'Conselho Municipal de Economia Solidária', value: 'solidarity_economy' },
    { label: 'Conselho Municipal de Meio Ambiente', value: 'environment' },
    { label: 'Conselho Municipal de Alimentação Escolar', value: 'school_feeding' },
    { label: 'Outro', value: 'other' },
];

const Step3: React.FC<Step3Props> = ({ hasCmdrs, disabled, onNext, onBack, onChange, data }) => {
    // Conselhos existentes
    const [existingCouncils, setExistingCouncils] = useState<string[]>(data?.existingCouncils || []);
    const [otherExistingCouncil, setOtherExistingCouncil] = useState(data?.otherExistingCouncil || '');
    // Conselhos com assento
    const [seatedCouncils, setSeatedCouncils] = useState<string[]>(data?.seatedCouncils || []);
    const [otherSeatedCouncil, setOtherSeatedCouncil] = useState(data?.otherSeatedCouncil || '');
    // Consórcio
    const [isConsortiumMember, setIsConsortiumMember] = useState<boolean | null>(
        typeof data?.isConsortiumMember === 'boolean' ? data.isConsortiumMember : null
    );
    const [participatesActivities, setParticipatesActivities] = useState<boolean | null>(
        typeof data?.participatesActivities === 'boolean' ? data.participatesActivities : null
    );
    const [activitiesDescription, setActivitiesDescription] = useState(data?.activitiesDescription || '');
    const [hasSusaf, setHasSusaf] = useState<boolean | null>(
        typeof data?.hasSusaf === 'boolean' ? data.hasSusaf : null
    );
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Atualiza os estados internos quando o prop data mudar
    useEffect(() => {
        setExistingCouncils(data?.existingCouncils || []);
        setOtherExistingCouncil(data?.otherExistingCouncil || '');
        setSeatedCouncils(data?.seatedCouncils || []);
        setOtherSeatedCouncil(data?.otherSeatedCouncil || '');
        setIsConsortiumMember(typeof data?.isConsortiumMember === 'boolean' ? data.isConsortiumMember : null);
        setParticipatesActivities(typeof data?.participatesActivities === 'boolean' ? data.participatesActivities : null);
        setActivitiesDescription(data?.activitiesDescription || '');
        setHasSusaf(typeof data?.hasSusaf === 'boolean' ? data.hasSusaf : null);
    }, [data]);

    useEffect(() => {
        onChange({
            existingCouncils,
            otherExistingCouncil,
            seatedCouncils,
            otherSeatedCouncil,
            isConsortiumMember,
            participatesActivities,
            activitiesDescription,
            hasSusaf,
        });
        // eslint-disable-next-line
    }, [
        existingCouncils,
        otherExistingCouncil,
        seatedCouncils,
        otherSeatedCouncil,
        isConsortiumMember,
        participatesActivities,
        activitiesDescription,
        hasSusaf,
    ]);

    // Handlers para checkboxes
    const handleCouncilChange = (
        value: string,
        selected: string[],
        setSelected: React.Dispatch<React.SetStateAction<string[]>>,
        setOther: React.Dispatch<React.SetStateAction<string>>
    ) => {
        setSelected(prev =>
            prev.includes(value)
                ? prev.filter(v => v !== value)
                : [...prev, value]
        );
        if (value !== 'other') setOther('');
    };

    // Validação
    const validate = () => {
        const newErrors: { [key: string]: string } = {};
        if (existingCouncils.length === 0) newErrors.existingCouncils = 'Campo obrigatório';
        if (existingCouncils.includes('other') && !otherExistingCouncil) newErrors.otherExistingCouncil = 'Por favor, especifique';
        if (seatedCouncils.length === 0) newErrors.seatedCouncils = 'Campo obrigatório';
        if (seatedCouncils.includes('other') && !otherSeatedCouncil) newErrors.otherSeatedCouncil = 'Por favor, especifique';
        return newErrors;
    };

    const handleSubmit = () => {
        const validation = validate();
        setErrors(validation);
        if (Object.keys(validation).length > 0) return;
        setErrors({});
        onNext();
    };

    return (
        <Box>
            <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Conselhos e Consórcio
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {/* Conselhos existentes */}
                <FormControl component="fieldset" fullWidth margin="normal" required>
                    <FormLabel
                        component="legend"
                        sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                        Quais outros Conselhos Municipais existem no município e estão funcionando?
                    </FormLabel>
                    <FormGroup>
                        {councilOptions.map(opt => (
                            <FormControlLabel
                                key={opt.value}
                                control={
                                    <Checkbox
                                        checked={existingCouncils.includes(opt.value)}
                                        onChange={() =>
                                            handleCouncilChange(opt.value, existingCouncils, setExistingCouncils, setOtherExistingCouncil)
                                        }
                                        value={opt.value}
                                        disabled={disabled}
                                    />
                                }
                                label={opt.label}
                            />
                        ))}
                    </FormGroup>
                    {errors.existingCouncils && <Typography color="error">{errors.existingCouncils}</Typography>}
                </FormControl>
                {existingCouncils.includes('other') && (
                    <TextField
                        label="Outro (especifique)"
                        value={otherExistingCouncil}
                        onChange={e => setOtherExistingCouncil(e.target.value)}
                        error={!!errors.otherExistingCouncil}
                        helperText={errors.otherExistingCouncil}
                        fullWidth
                        margin="dense"
                        disabled={disabled}
                        required
                    />
                )}

                {/* Conselhos com assento */}
                <FormControl component="fieldset" fullWidth margin="normal" required>
                    <FormLabel
                        component="legend"
                        sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                        Em quais outros Conselhos Municipais a Secretaria/Órgão Municipal de agricultura familiar têm assento?
                    </FormLabel>
                    <FormGroup>
                        {councilOptions.map(opt => (
                            <FormControlLabel
                                key={opt.value}
                                control={
                                    <Checkbox
                                        checked={seatedCouncils.includes(opt.value)}
                                        onChange={() =>
                                            handleCouncilChange(opt.value, seatedCouncils, setSeatedCouncils, setOtherSeatedCouncil)
                                        }
                                        value={opt.value}
                                        disabled={disabled}
                                    />
                                }
                                label={opt.label}
                            />
                        ))}
                    </FormGroup>
                    {errors.seatedCouncils && <Typography color="error">{errors.seatedCouncils}</Typography>}
                </FormControl>
                {seatedCouncils.includes('other') && (
                    <TextField
                        label="Outro (especifique)"
                        value={otherSeatedCouncil}
                        onChange={e => setOtherSeatedCouncil(e.target.value)}
                        error={!!errors.otherSeatedCouncil}
                        helperText={errors.otherSeatedCouncil}
                        fullWidth
                        margin="dense"
                        disabled={disabled}
                        required
                    />
                )}

                {/* Consórcio
                <FormControl component="fieldset" fullWidth margin="normal" required>
                    <FormLabel
                        component="legend"
                        sx={{ color: 'text.primary', fontWeight: 600 }}
                    >
                        O município é membro de algum Consórcio Intermunicipal de Desenvolvimento Econômico, Social e Ambiental - CIDESA?
                    </FormLabel>
                    <RadioGroup
                        value={isConsortiumMember === null ? '' : isConsortiumMember.toString()}
                        onChange={e => {
                            const value = e.target.value === 'true';
                            setIsConsortiumMember(value);
                            setParticipatesActivities(null);
                            setActivitiesDescription('');
                            setHasSusaf(null);
                        }}
                    >
                        <FormControlLabel value="true" control={<Radio disabled={disabled} />} label="Sim" />
                        <FormControlLabel value="false" control={<Radio disabled={disabled} />} label="Não" />
                    </RadioGroup>
                    {errors.isConsortiumMember && <Typography color="error">{errors.isConsortiumMember}</Typography>}
                </FormControl>

                {isConsortiumMember && (
                    <>
                        <FormControl component="fieldset" fullWidth margin="normal" required>
                            <FormLabel
                                component="legend"
                                sx={{ color: 'text.primary', fontWeight: 600 }}
                            >
                                O município participa de atividades em conjunto com o Consórcio?
                            </FormLabel>
                            <RadioGroup
                                value={participatesActivities === null ? '' : participatesActivities.toString()}
                                onChange={e => {
                                    const value = e.target.value === 'true';
                                    setParticipatesActivities(value);
                                    if (!value) setActivitiesDescription('');
                                }}
                            >
                                <FormControlLabel value="true" control={<Radio disabled={disabled} />} label="Sim" />
                                <FormControlLabel value="false" control={<Radio disabled={disabled} />} label="Não" />
                            </RadioGroup>
                            {errors.participatesActivities && <Typography color="error">{errors.participatesActivities}</Typography>}
                        </FormControl>
                        {participatesActivities && (
                            <TextField
                                label="Descreva as atividades em conjunto"
                                value={activitiesDescription}
                                onChange={e => setActivitiesDescription(e.target.value)}
                                error={!!errors.activitiesDescription}
                                helperText={errors.activitiesDescription}
                                fullWidth
                                margin="dense"
                                disabled={disabled}
                                required
                                multiline
                                minRows={2}
                            />
                        )}
                        <FormControl component="fieldset" fullWidth margin="normal" required>
                            <FormLabel
                                component="legend"
                                sx={{ color: 'text.primary', fontWeight: 600 }}
                            >
                                O consórcio intermunicipal fez adesão ao SUSAF/MT?
                            </FormLabel>
                            <RadioGroup
                                value={hasSusaf === null ? '' : hasSusaf.toString()}
                                onChange={e => setHasSusaf(e.target.value === 'true')}
                            >
                                <FormControlLabel value="true" control={<Radio disabled={disabled} />} label="Sim" />
                                <FormControlLabel value="false" control={<Radio disabled={disabled} />} label="Não" />
                            </RadioGroup>
                            {errors.hasSusaf && <Typography color="error">{errors.hasSusaf}</Typography>}
                        </FormControl>
                    </>
                )} */}

                <Box display="flex" justifyContent="flex-end" mt={3} gap={2}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={onBack}
                        disabled={disabled}
                    >
                        Voltar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={disabled}
                    >
                        Salvar e continuar
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default Step3;